// frontend/src/components/FormatEditor.tsx
import React, { useEffect, useMemo, useState } from "react";
import { X } from "lucide-react";
import { API_BASE_URL } from "@/config";
import { auth } from "@/lib/firebase";
import toast from "react-hot-toast";
export type ExplanationMode = "answer_only" | "full";
export type LayoutStyle = "stacked" | "card" | "leftRight";

export interface FormatOverrides {
  explanation_mode: ExplanationMode;
  layout: LayoutStyle;
  font_question: number;
  font_answer: number;
  font_explanation: number;
  wrap_frac_question: number; // 0..1
  wrap_frac_explanation: number; // 0..1
  show_countdown: boolean;
  show_progress: boolean;
  accent: string; // css color (e.g., #10b981)
}

export interface FormatEditorProps {
  open: boolean; // modal visibility
  videoUrl: string;
  videoFormat?: "9:16" | "16:9" | "1:1";
  initial?: Partial<FormatOverrides>;
  onClose: () => void;
  onRendered?: (newUrl: string) => void;
}

const DEFAULTS: FormatOverrides = {
  explanation_mode: "answer_only",
  layout: "stacked",
  font_question: 38,
  font_answer: 28,
  font_explanation: 24,
  wrap_frac_question: 0.92,
  wrap_frac_explanation: 0.92,
  show_countdown: false,
  show_progress: true,
  accent: "#10b981",
};

type SizeKey = "font_question" | "font_answer" | "font_explanation";

type Mode = "modal" | "page";

type CoreProps = {
  mode: Mode;
  // modal uses `videoUrl` from props, page can let user type it
  videoUrl: string;
  setVideoUrl?: (v: string) => void; // only used in page mode
  videoFormat?: "9:16" | "16:9" | "1:1";
  initial?: Partial<FormatOverrides>;
  onClose: () => void;
  onRendered?: (newUrl: string) => void;
};

function EditorCore({ mode, videoUrl, setVideoUrl, videoFormat = "9:16", initial, onClose, onRendered }: CoreProps) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [tab, setTab] = useState<"layout" | "typography" | "advanced">("layout");

  const [v, setV] = useState<FormatOverrides>({ ...DEFAULTS, ...(initial || {}) });

  useEffect(() => {
    setV({ ...DEFAULTS, ...(initial || {}) });
    setErr(null);
    setBusy(false);
    setTab("layout");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initial]);

  const reset = () => setV({ ...DEFAULTS, ...(initial || {}) });
  const set = <K extends keyof FormatOverrides>(k: K, val: FormatOverrides[K]) => setV((p) => ({ ...p, [k]: val }));

  const secsEstimate = useMemo(() => (v.explanation_mode === "answer_only" ? 5 : 12), [v.explanation_mode]);

  const sizeControls: ReadonlyArray<{ k: SizeKey; label: string; min: number; max: number }> = useMemo(
    () => [
      { k: "font_question", label: "Question size", min: 20, max: 64 },
      { k: "font_answer", label: "Answer size", min: 16, max: 48 },
      { k: "font_explanation", label: "Explanation size", min: 14, max: 40 },
    ],
    []
  );

  const submit = async () => {
    if (!videoUrl) {
      toast.error("Missing source video URL");
      return;
    }
    try {
      setBusy(true);
      setErr(null);
      const token = await auth.currentUser?.getIdToken(true).catch(() => undefined);

      const prefer = `${API_BASE_URL}/api/video/regenerate`;
      const fallback = `${API_BASE_URL}/api/video/generate-video`;
      const body = {
        source_url: videoUrl,
        video_format: videoFormat,
        overrides: v,
        tiktok_friendly: videoFormat === "9:16" || v.explanation_mode === "answer_only",
      } as const;

      let resp = await fetch(prefer, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify(body),
      });

      if (!resp.ok) {
        resp = await fetch(fallback, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          body: JSON.stringify(body),
        });
      }

      if (!resp.ok) {
        const txt = await resp.text().catch(() => "");
        throw new Error(`Render failed ${resp.status}${txt ? `: ${txt}` : ""}`);
      }

      const j = (await resp.json().catch(() => ({}))) as any;
      const newUrl: string | undefined = j.video_url ?? j.url ?? j.videoUrl;
      if (!newUrl) throw new Error("Backend did not return a video URL.");

      toast.success("Rendering started / ready");
      onRendered?.(newUrl);
      onClose();
    } catch (e: any) {
      const msg = e?.message || "Unable to start render";
      setErr(msg);
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className={mode === "modal" ? "" : "max-w-5xl mx-auto w-full"}>
      {mode === "page" && (
        <header className="flex items-center justify-between px-2 sm:px-0 py-3">
          <h1 className="text-xl font-semibold">Layout Editor</h1>
          <div className="flex items-center gap-2">
            <button onClick={onClose} className="px-3 py-1.5 rounded border">Close</button>
          </div>
        </header>
      )}

      {/* Source controls for page mode */}
      {mode === "page" && (
        <div className="px-2 sm:px-0 pb-2">
          <div className="grid sm:grid-cols-[1fr_auto] gap-2">
            <input
              type="url"
              placeholder="Paste a source video URL…"
              value={videoUrl}
              onChange={(e) => setVideoUrl?.(e.currentTarget.value)}
              className="w-full border rounded px-3 py-2 bg-white dark:bg-slate-800"
            />
            <select
              value={videoFormat}
              onChange={() => {}}
              className="border rounded px-3 py-2 bg-white dark:bg-slate-800"
              aria-label="Video format"
            >
              <option value="9:16">TikTok / Reels (9:16)</option>
              <option value="16:9">YouTube (16:9)</option>
              <option value="1:1">Square (1:1)</option>
            </select>
          </div>
        </div>
      )}

      <div className="px-4 pt-3">
        {/* tabs */}
        <div className="flex gap-2 mb-3">
          {(["layout", "typography", "advanced"] as const).map((t) => (
            <button
              key={t}
              onClick={() => setTab(t)}
              className={[
                "px-3 py-1.5 rounded-full border text-sm",
                tab === t ? "bg-black text-white dark:bg-white dark:text-black" : "hover:bg-gray-50 dark:hover:bg-gray-800",
              ].join(" ")}
              aria-pressed={tab === t}
            >
              {t}
            </button>
          ))}
          <span className="ml-auto text-xs text-gray-500">Est. per-question: ~{secsEstimate}s</span>
        </div>
      </div>

      <div className="px-4 pb-2 max-h-[60vh] overflow-y-auto">
        {tab === "layout" && (
          <section className="grid md:grid-cols-2 gap-4">
            <div className="p-4 rounded-xl border bg-white/70 dark:bg-slate-800/60">
              <label className="block text-sm font-medium mb-2">Explanation</label>
              <select
                value={v.explanation_mode}
                onChange={(e) => set("explanation_mode", e.target.value as ExplanationMode)}
                className="w-full border rounded px-3 py-2 bg-white dark:bg-slate-800"
              >
                <option value="answer_only">Answer only (short)</option>
                <option value="full">Full explanation</option>
              </select>
            </div>

            <div className="p-4 rounded-xl border bg-white/70 dark:bg-slate-800/60">
              <label className="block text-sm font-medium mb-2">Layout style</label>
              <select
                value={v.layout}
                onChange={(e) => set("layout", e.target.value as LayoutStyle)}
                className="w-full border rounded px-3 py-2 bg-white dark:bg-slate-800"
              >
                <option value="stacked">Stacked (title then options)</option>
                <option value="card">Card (boxed)</option>
                <option value="leftRight">Left/Right split</option>
              </select>
            </div>

            <div className="p-4 rounded-xl border bg-white/70 dark:bg-slate-800/60">
              <label className="block text-sm font-medium mb-1">Wrap width — Question</label>
              <input
                type="range"
                min={0.5}
                max={1}
                step={0.01}
                value={v.wrap_frac_question}
                onChange={(e) => set("wrap_frac_question", Number(e.target.value))}
                className="w-full"
              />
              <div className="text-xs text-gray-500 mt-1">{Math.round(v.wrap_frac_question * 100)}%</div>
            </div>

            <div className="p-4 rounded-xl border bg-white/70 dark:bg-slate-800/60">
              <label className="block text-sm font-medium mb-1">Wrap width — Explanation</label>
              <input
                type="range"
                min={0.5}
                max={1}
                step={0.01}
                value={v.wrap_frac_explanation}
                onChange={(e) => set("wrap_frac_explanation", Number(e.target.value))}
                className="w-full"
              />
              <div className="text-xs text-gray-500 mt-1">{Math.round(v.wrap_frac_explanation * 100)}%</div>
            </div>

            <div className="p-4 rounded-xl border bg-white/70 dark:bg-slate-800/60">
              <label className="block text-sm font-medium mb-2">Accent colour</label>
              <input type="color" value={v.accent} onChange={(e) => set("accent", e.target.value)} className="h-9 w-16 cursor-pointer" />
            </div>

            <div className="p-4 rounded-xl border bg-white/70 dark:bg-slate-800/60">
              <label className="block text-sm font-medium mb-2">Elements</label>
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={v.show_progress} onChange={(e) => set("show_progress", e.target.checked)} />
                Show progress bar
              </label>
              <label className="flex items-center gap-2 text-sm mt-1">
                <input type="checkbox" checked={v.show_countdown} onChange={(e) => set("show_countdown", e.target.checked)} />
                Show countdown
              </label>
            </div>
          </section>
        )}

        {tab === "typography" && (
          <section className="grid md:grid-cols-3 gap-4">
            {sizeControls.map((f) => (
              <div key={f.k} className="p-4 rounded-xl border bg-white/70 dark:bg-slate-800/60">
                <label className="block text-sm font-medium mb-1">{f.label}</label>
                <input
                  type="range"
                  min={f.min}
                  max={f.max}
                  step={1}
                  value={v[f.k]}
                  onChange={(e) => set(f.k, Number(e.currentTarget.value))}
                  className="w-full"
                />
                <div className="text-xs text-gray-500 mt-1">{v[f.k]} px</div>
              </div>
            ))}
          </section>
        )}

        {tab === "advanced" && (
          <section className="p-4 rounded-xl border bg-white/70 dark:bg-slate-800/60 space-y-2">
            <p className="text-sm text-gray-600 dark:text-gray-300">These options map to backend render overrides. If unsupported, they are safely ignored.</p>
            <pre className="text-xs bg-black/5 dark:bg-white/5 p-2 rounded overflow-auto">{JSON.stringify(v, null, 2)}</pre>
          </section>
        )}

        {err && <div className="mt-3 text-sm text-red-600">{err}</div>}
      </div>

      <div className="flex items-center justify-between gap-2 p-4 border-t border-black/10 dark:border-white/10">
        <button onClick={reset} className="px-3 py-1.5 rounded border">Reset</button>
        <div className="flex items-center gap-2">
          <button onClick={onClose} className="px-3 py-1.5 rounded border">{mode === "modal" ? "Cancel" : "Close"}</button>
          <button onClick={submit} disabled={busy} className="px-4 py-1.5 rounded bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-60">
            {busy ? "Rendering…" : "Re-render"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────
// Modal export (backwards compatible default)
export default function FormatEditor({ open, videoUrl, videoFormat = "9:16", initial, onClose, onRendered }: FormatEditorProps) {
  if (!open) return null;
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-labelledby="format-editor-title"
    >
      <div
        className="w-full max-w-3xl rounded-2xl bg-white dark:bg-slate-900 shadow-xl border border-black/10 dark:border-white/10"
        onClick={(e) => e.stopPropagation()}
        aria-busy={false}
      >
        <div className="flex items-center justify-between p-4 border-b border-black/10 dark:border-white/10">
          <h2 id="format-editor-title" className="text-lg font-semibold">Edit Question Format</h2>
          <button onClick={onClose} className="p-1 rounded hover:bg-black/5 dark:hover:bg-white/10" aria-label="Close">
            <X size={18} />
          </button>
        </div>
        <EditorCore mode="modal" videoUrl={videoUrl} videoFormat={videoFormat} initial={initial} onClose={onClose} onRendered={onRendered} />
      </div>
    </div>
  );
}



